import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {graphql} from 'react-relay';
import {withRouter, RouteComponentProps} from 'react-router-dom';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import Util from '../../forecast-app/shared/util/util';
import CreateApiKeyMutation from '../../mutations/create_api_key_mutation';
import DrawerHeader from './DrawerHeader';
import DrawerBody from './DrawerBody';
import ChatPanel from './ChatPanel';

const DRAWER_WIDTH = 480;

const DrawerContainer = styled.div<{isOpen: boolean}>`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: ${DRAWER_WIDTH}px;
	transform: ${({isOpen}) => (isOpen ? 'translateX(0)' : `translateX(${DRAWER_WIDTH}px)`)};
	transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
	border-left: 1px solid #d3d3df;
	box-shadow: 0px 2px 2px rgba(35, 35, 52, 0.12);
	background: #ffffff;
	display: flex;
	flex-direction: column;
	z-index: 100;
`;

const AgentDrawerQuery = graphql`
	query AgentDrawerQuery {
		viewer {
			actualPersonId
			backendId
			company {
				id
				name
				apiKeys(first: 1000) @connection(key: "AgentDrawer_apiKeys") {
					edges {
						node {
							id
							key
							active
							createdBy {
								id
							}
						}
					}
				}
			}
		}
	}
`;

type DrawerContentProps = {
	viewer: {
		backendId: number;
		company: {
			id: string;
			name: string;
			apiKeys: {
				edges: Array<{
					node: {
						id: string;
						key: string;
						active: boolean;
						createdBy: {id: string} | null;
					};
				}>;
			} | null;
		};
	};
	history: RouteComponentProps['history'];
	isOpen: boolean;
	onClose: () => void;
};

const DrawerContent = ({viewer, history, isOpen, onClose}: DrawerContentProps): JSX.Element => {
	const edges = viewer.company?.apiKeys?.edges ?? [];
	const activeApiKey = (() => {
		const mine = edges.find(e => {
			const node = e?.node;
			if (!node?.active || !node?.createdBy?.id) return false;
			return Util.getIdFromBase64String(node.createdBy.id) === viewer.backendId;
		});
		return mine ? mine.node.key : '';
	})();

	const apiKeysLoaded = viewer.company?.apiKeys != null;

	const [hasLlmResponse, setHasLlmResponse] = useState(false);
	const clearRef = useRef<(() => void) | null>(null);
	const regenerateRef = useRef<(() => void) | null>(null);
	const registerClear = useCallback((fn: () => void) => {
		clearRef.current = fn;
	}, []);
	const registerRegenerate = useCallback((fn: () => void) => {
		regenerateRef.current = fn;
	}, []);
	const handleClearChat = useCallback(() => {
		clearRef.current?.();
	}, []);
	const handleRegenerateLastReply = useCallback(() => {
		regenerateRef.current?.();
	}, []);

	const companyId = viewer.company.id;
	const companyName = viewer.company.name;
	const provisioningRef = useRef(false);
	useEffect(() => {
		if (isOpen && apiKeysLoaded && !activeApiKey && !provisioningRef.current) {
			provisioningRef.current = true;
			Util.CommitMutation(
				CreateApiKeyMutation,
				companyId,
				() => {
					provisioningRef.current = false;
				},
				undefined,
				undefined,
				() => {
					provisioningRef.current = false;
				}
			);
		}
		if (!isOpen || activeApiKey) {
			provisioningRef.current = false;
		}
	}, [isOpen, apiKeysLoaded, activeApiKey, companyId]);

	return (
		<div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
			<DrawerHeader
				onClose={onClose}
				onClearChat={handleClearChat}
				onRegenerateLastReply={handleRegenerateLastReply}
				canRegenerate={hasLlmResponse}
			/>
			<DrawerBody>
				<ChatPanel
					apiKey={activeApiKey}
					viewerPersonId={viewer.backendId}
					companyId={companyId}
					companyName={companyName}
					history={history}
					isProvisioning={apiKeysLoaded && !activeApiKey}
					onRegisterClear={registerClear}
					onRegisterRegenerate={registerRegenerate}
					onHasLlmResponseChange={setHasLlmResponse}
				/>
			</DrawerBody>
		</div>
	);
};

const AgentDrawerInner = ({history}: RouteComponentProps): JSX.Element => {
	const {isOpen, closeDrawer} = useAgentDrawerContext();

	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeDrawer();
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen, closeDrawer]);

	return (
		<DrawerContainer isOpen={isOpen} data-cy="agent-drawer">
			<ForecastQueryRenderer
				key="agent-drawer-query"
				query={AgentDrawerQuery}
				showLoader={false}
				render={(relayProps: {viewer: DrawerContentProps['viewer']} | null) => {
					if (!relayProps?.viewer) return null;
					return <DrawerContent viewer={relayProps.viewer} history={history} isOpen={isOpen} onClose={closeDrawer} />;
				}}
			/>
		</DrawerContainer>
	);
};

export default withRouter(AgentDrawerInner);
